import { graphScore } from "./seaBassPresentation";

const HOUR_MS = 3600000;
export const LOCAL_DAY_TICKS = [0, 4, 8, 12, 16, 20, 24];

// The location's wall clock is used for labels, never the device timezone.
export const localChartTime = (timestamp, timeZone) => {
  const millis = Date.parse(timestamp);
  if (!Number.isFinite(millis)) return null;
  try {
    if (!timeZone) throw new Error("No forecast zone");
    const parts = Object.fromEntries(new Intl.DateTimeFormat("en-GB", {
      timeZone, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(millis).map(({ type, value }) => [type, value]));
    return { date: `${parts.year}-${parts.month}-${parts.day}`, hour: Number(parts.hour) + Number(parts.minute) / 60 };
  } catch (_error) {
    const match = String(timestamp).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/);
    return match ? { date: match[1], hour: Number(match[2]) + Number(match[3]) / 60 } : null;
  }
};

export const chartScore = (value) => {
  const score = graphScore(value);
  return Number.isFinite(score) && score >= 0 && score <= 100 ? score : null;
};

// Find actual local midnight boundaries, including DST. Searching date keys
// avoids assuming a fixed UTC offset or an always-24-hour local calendar day.
export const localDayAxis = (date, timeZone, referenceTimestamp) => {
  if (!date) return null;
  const nominal = Date.parse(`${date}T00:00:00Z`);
  if (!Number.isFinite(nominal)) return null;
  let local;
  let verifiedZone = true;
  try {
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: timeZone || "invalid", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    });
    local = (millis) => {
      const p = Object.fromEntries(formatter.formatToParts(millis).map(({ type, value }) => [type, value]));
      return { date: `${p.year}-${p.month}-${p.day}`, hour: Number(p.hour), minute: Number(p.minute) };
    };
  } catch (_error) {
    // Legacy responses have an explicit timestamp offset but no IANA zone.
    // Retain that offset, not the device's; do not claim verified day coverage.
    verifiedZone = false;
    const wall = String(referenceTimestamp || "").match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
    if (!wall || !/(Z|[+-]\d{2}:\d{2})$/.test(referenceTimestamp)) return null;
    const offset = Date.parse(`${wall[1]}:00Z`) - Date.parse(referenceTimestamp);
    local = (millis) => {
      const stamp = new Date(millis + offset).toISOString();
      return { date: stamp.slice(0, 10), hour: Number(stamp.slice(11, 13)), minute: Number(stamp.slice(14, 16)) };
    };
  }
  const boundary = (target, center) => {
    let lo = center - 36 * HOUR_MS;
    let hi = center + 36 * HOUR_MS;
    while (hi - lo > 1) {
      const mid = Math.floor((hi + lo) / 2);
      if (local(mid).date < target) lo = mid;
      else hi = mid;
    }
    return hi;
  };
  const nextDate = new Date(nominal + 24 * HOUR_MS).toISOString().slice(0, 10);
  const start = boundary(date, nominal);
  const end = boundary(nextDate, nominal + 24 * HOUR_MS);
  const ticks = [];
  // Minute resolution accommodates non-whole-hour UTC offsets and DST shifts.
  for (let stamp = start; stamp < end; stamp += 60000) {
    const p = local(stamp);
    if (p.minute === 0 && p.hour % 4 === 0) ticks.push({ hour: p.hour, timestampMs: stamp });
  }
  ticks.push({ hour: 24, timestampMs: end });
  return { start, end, ticks, verifiedZone, expectedHours: Math.ceil((end - start) / HOUR_MS) };
};

export const selectLocalDayHours = (source, timeZone, localDate, currentTimestamp) => {
  const seen = new Set();
  const ordered = (Array.isArray(source) ? source : [])
    .map((point) => ({ ...point, timestampMs: Date.parse(point?.timestamp), local: localChartTime(point?.timestamp, timeZone) }))
    .filter((point) => point.local && Number.isFinite(point.timestampMs))
    .sort((a, b) => a.timestampMs - b.timestampMs)
    .filter((point) => {
      if (seen.has(point.timestampMs)) return false;
      seen.add(point.timestampMs);
      return true;
    });
  const currentDate = localChartTime(currentTimestamp, timeZone)?.date;
  const date = localDate || (ordered.some((point) => point.local.date === currentDate) ? currentDate : ordered[0]?.local.date);
  const hours = ordered.filter((point) => point.local.date === date).map((point, index, selected) => ({
    ...point,
    breakBefore: Object.fromEntries(["fishingOpportunity", "activity"].map((field) => [
      field, index > 0 && point.timestampMs - selected[index - 1].timestampMs > HOUR_MS,
    ])),
  }));
  const axis = localDayAxis(date, timeZone, hours[0]?.timestamp);
  const complete = Boolean(axis?.verifiedZone && hours.length === axis.expectedHours &&
    hours.every((point, i) => point.timestampMs === axis.start + i * HOUR_MS));
  return { date, hours, axis, complete };
};

// Display convention only: +/-30 minutes, not civil twilight or feeding duration.
export const LIGHT_TRANSITION_HALF_WIDTH_MS = 30 * 60000;
export const lightCycleBands = (cycle, axis) => {
  if (!axis || cycle?.source !== "local_noaa_meeus") return [];
  if (["polar_day", "polar_night"].includes(cycle.state)) {
    if (cycle.sunrise || cycle.sunset) return [];
    return [{ kind: cycle.state, start: axis.start, end: axis.end }];
  }
  if (cycle.state !== "ordinary") return [];
  const events = ["sunrise", "sunset"].filter((kind) => cycle[kind] != null)
    .map((kind) => ({ kind, timestamp: /(?:Z|[+-]\d{2}:\d{2})$/.test(cycle[kind]) ? Date.parse(cycle[kind]) : NaN }))
    .sort((a, b) => a.timestamp - b.timestamp);
  if (!events.length || events.some((e) => !Number.isFinite(e.timestamp) || e.timestamp < axis.start || e.timestamp >= axis.end)) return [];
  const bands = [];
  let cursor = axis.start;
  let kind = events[0].kind === "sunrise" ? "night" : "day";
  events.forEach((event) => {
    bands.push({ kind, start: cursor, end: event.timestamp });
    cursor = event.timestamp;
    kind = event.kind === "sunrise" ? "day" : "night";
  });
  bands.push({ kind, start: cursor, end: axis.end });
  return [...bands, ...events.map((event) => ({
    kind: event.kind,
    start: Math.max(axis.start, event.timestamp - LIGHT_TRANSITION_HALF_WIDTH_MS),
    end: Math.min(axis.end, event.timestamp + LIGHT_TRANSITION_HALF_WIDTH_MS),
  }))].filter((band) => band.end > band.start);
};

export const formatLocalDayTick = (hour, locale = "en") =>
  new Intl.DateTimeFormat(locale, { timeZone: "UTC", hour: "numeric" })
    .format(new Date(Date.UTC(2020, 0, 1, hour % 24)));

export const selectRollingHours = (source, timeZone, start, end) => {
  const first = Date.parse(start), last = Date.parse(end);
  if (!Number.isFinite(first) || last - first !== 86400000) return { hours: [], axis: null, complete: false };
  const seen = new Set();
  const hours = (Array.isArray(source) ? source : []).map((p) => ({ ...p, timestampMs: Date.parse(p.timestamp) }))
    .filter((p) => p.timestampMs >= first && p.timestampMs < last)
    .sort((a, b) => a.timestampMs - b.timestampMs)
    .filter((p) => { if (seen.has(p.timestampMs)) return false; seen.add(p.timestampMs); return true; })
    .map((p, i, ordered) => ({ ...p, local: localChartTime(p.timestamp, timeZone),
      breakBefore: { activity: i > 0 && p.timestampMs - ordered[i - 1].timestampMs > HOUR_MS,
        fishingOpportunity: i > 0 && p.timestampMs - ordered[i - 1].timestampMs > HOUR_MS } }));
  return { hours, complete: hours.length === 24 && hours.every((p, i) => p.timestampMs === first + i * HOUR_MS),
    axis: { start: first, end: last, expectedHours: 24,
      ticks: LOCAL_DAY_TICKS.map((hour) => ({ timestampMs: first + hour * HOUR_MS })) } };
};

export const windowLightContext = (axis, timeZone, days) => {
  const bands = [], midnights = [];
  let missing = false;
  if (!axis) return { bands, midnights, missing: true };
  let cursor = axis.start;
  while (cursor < axis.end) {
    const date = localChartTime(new Date(cursor).toISOString(), timeZone)?.date;
    const localAxis = localDayAxis(date, timeZone);
    if (!localAxis || localAxis.end <= cursor) { missing = true; break; }
    const light = days?.find((day) => day.localDate === date)?.lightCycle;
    const segments = lightCycleBands(light, localAxis);
    if (!segments.length) missing = true;
    bands.push(...segments.map((band) => ({ ...band, start: Math.max(axis.start, band.start), end: Math.min(axis.end, band.end) }))
      .filter((band) => band.end > band.start));
    if (localAxis.start > axis.start) midnights.push({ timestampMs: localAxis.start, date });
    cursor = localAxis.end;
  }
  return { bands, midnights, missing };
};
